import { CircularProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import YAML from "yaml";

import { FileRecord } from "@/shared/domain/files";
import { MAX_FILE_SIZE_HLJS } from "@/shared/domain/files/FileRecord";
import { getHljsLanguage } from "@/shared/domain/files/fileTypes";

import styles from "./TextContent.module.css";

const HighlightedCodeBody = React.lazy(
  () => import("@/shared/components/HighlightedCode/HighlightedCodeBody"),
);
const JsonTreeView = React.lazy(
  () => import("@/shared/components/JsonTreeView/JsonTreeView"),
);

type ViewMode = "tree" | "source";

interface TextContentProps {
  fileRecord: FileRecord;
  signedUrl: URL | undefined;
}

export const TextContent: React.FC<TextContentProps> = ({
  fileRecord,
  signedUrl,
}) => {
  const contentsQuery = useQuery({
    queryKey: ["contents", fileRecord.file_id],
    queryFn: ({ signal }) => {
      if (!signedUrl) {
        return;
      }
      return fetch(signedUrl, { signal }).then((response) => response.text());
    },
    enabled: signedUrl !== undefined,
  });

  if (contentsQuery.isError) {
    return (
      <div className={styles.messageContainer}>
        <Typography>Failed to load file contents.</Typography>
      </div>
    );
  }

  // `data` is undefined while the fetch is in flight or before the signed URL
  // resolves (the query is disabled until then, so `isLoading` doesn't cover it).
  if (contentsQuery.data === undefined) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress size="1.75rem" />
      </div>
    );
  }

  const data = contentsQuery.data;
  const language = getHljsLanguage(fileRecord.relative_path);

  return (
    <Body
      data={data}
      language={language}
      withinPreviewBudget={data.length <= MAX_FILE_SIZE_HLJS}
    />
  );
};

interface BodyProps {
  data: string;
  language: ReturnType<typeof getHljsLanguage>;
  /**
   * Whether the file is small enough for expensive preview operations
   * (syntax highlighting and JSON/YAML tree parsing). Both stall the main
   * thread on multi-MB inputs, so we share a single threshold.
   */
  withinPreviewBudget: boolean;
}

function Body({ data, language, withinPreviewBudget }: BodyProps) {
  const treeSrc = React.useMemo(() => {
    if (!withinPreviewBudget) {
      return null;
    }
    if (language === "json" || language === "yaml") {
      return parseToTreeSrc(data, language);
    }
    return null;
  }, [data, language, withinPreviewBudget]);

  const supportsTree = treeSrc !== null;
  const [viewMode, setViewMode] = React.useState<ViewMode>(
    supportsTree ? "tree" : "source",
  );
  // No need to re-check `supportsTree` here: the render below already guards
  // the tree branch on `treeSrc !== null`, and the toggle is only shown when
  // `supportsTree` is true.
  const showTree = viewMode === "tree";

  return (
    <div className={styles.wrapper}>
      {supportsTree ? (
        <ViewToggle mode={viewMode} onChange={setViewMode} />
      ) : null}

      {showTree && treeSrc !== null ? (
        <div className={styles.treeContainer}>
          <React.Suspense fallback={null}>
            <JsonTreeView src={treeSrc} />
          </React.Suspense>
        </div>
      ) : (
        <pre className={styles.preview}>
          {language !== null && withinPreviewBudget ? (
            <React.Suspense fallback={<code>{data}</code>}>
              <HighlightedCodeBody code={data} language={language} />
            </React.Suspense>
          ) : (
            data
          )}
        </pre>
      )}
    </div>
  );
}

interface ViewToggleProps {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
}

function ViewToggle({ mode, onChange }: ViewToggleProps) {
  return (
    <div className={styles.viewToggle} role="tablist" aria-label="View mode">
      <button
        type="button"
        role="tab"
        aria-selected={mode === "tree"}
        className={mode === "tree" ? styles.toggleActive : styles.toggleButton}
        onClick={() => {
          onChange("tree");
        }}
      >
        Tree
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === "source"}
        className={
          mode === "source" ? styles.toggleActive : styles.toggleButton
        }
        onClick={() => {
          onChange("source");
        }}
      >
        Source
      </button>
    </div>
  );
}

/**
 * Parse `data` as JSON or YAML and return the result if it's an object or
 * array (i.e. has structure worth tree-viewing). Returns `null` for parse
 * errors or primitive roots — the caller falls back to the source view.
 */
function parseToTreeSrc(
  data: string,
  language: "json" | "yaml",
): object | null {
  let parsed: unknown;
  try {
    parsed = language === "json" ? JSON.parse(data) : YAML.parse(data);
  } catch {
    return null;
  }
  if (parsed !== null && typeof parsed === "object") {
    return parsed;
  }
  return null;
}
